import { breakpoints, Button, Icon, Input, InputProps, Theme, useBelowBreakpoint } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { calcMinsInDaysAndHourAndMins } from 'services/Helpers';
import { ThemeContext } from 'styled-components';
import { cmsConfig } from 'views/CMS/CMSConfig';
import { DurationInputSC as T } from './DurationInputSC';

interface DurationInputProps {
    durationFromInput: (value: number) => void;
    value?: number;
    minimized?: boolean;
    maximized?: boolean;
    title?: string;
    cleareable?: boolean;
    info?: string;
    error?: boolean;
    shortenText?: boolean;
    step?: number;
    noMinutes?: boolean;
    debounce?: InputProps["debounce"];
}

type InputTime = { d: number, h: number, m: number };
const hpd = cmsConfig.trainingDurationHoursperDay;

const calcNewValue = (cur: InputTime) => {
    if (cur.m >= 60) {
        cur.h += Math.floor(cur.m / 60);
        cur.m = cur.m % 60;
    }
    if (cur.m < 0) {
        if (cur.h > 0 || cur.d > 0) {
            cur.m = 60 + cur.m;
            cur.h--;
        }
        else cur.m = 0;
    }
    if (cur.h >= hpd) {
        cur.d += Math.floor(cur.h / hpd);
        cur.h = cur.h % hpd;
    }
    if (cur.h < 0) {
        if (cur.d > 0) {
            cur.h = hpd + cur.h;
            cur.d--;
        }
        else
            cur.h = 0;
    }
    if (cur.d < 0)
        cur.d = 0;
    return cur;
}

const inputStateFromValue = (min?: number): InputTime => {
    const val = calcMinsInDaysAndHourAndMins(min, cmsConfig.trainingDurationHoursperDay);
    return ({
        d: val[0],
        h: val[1],
        m: val[2]
    })
}

const calcMinutes = (val: InputTime) => {
    return (val.d * cmsConfig.trainingDurationHoursperDay + val.h) * 60 + val.m;
}

const DurationInput = (p: DurationInputProps) => {
    const { durationFromInput, value, minimized,
        maximized, cleareable, title, info, error,
        step, shortenText, noMinutes, debounce } = p;
    const [inputState, setInputState] = React.useState<InputTime>({ m: 0, h: 0, d: 0 });
    const update = React.useMemo(() => _.debounce(durationFromInput, 200), [durationFromInput]);
    const theme = React.useContext<Theme>(ThemeContext);

    const { onChangeDays, onChangeHours, onChangeMinutes, onClear, countIcon } = React.useMemo(() => ({
        onChangeDays: (val: string | undefined) => {
            setInputState(i => {
                const x = parseInt(val ?? "0");
                const next = calcNewValue({ ...i, d: Number.isNaN(x) ? 0 : x })
                update(calcMinutes(next));
                return next;
            });
        },
        onChangeHours: (val: string | undefined) => {
            //console.log('val => ', val);

            setInputState(i => {
                const x = parseInt(val ?? "0");
                const next = calcNewValue({ ...i, h: Number.isNaN(x) ? 0 : x })
                update(calcMinutes(next));
                return next;
            });
        },
        onChangeMinutes: (val: string | undefined) => {
            setInputState(i => {
                const x = parseInt(val ?? "0");
                const next = calcNewValue({ ...i, m: Number.isNaN(x) ? 0 : x })
                update(calcMinutes(next));
                return next;
            });
        },
        onClear: () => {
            setInputState(i => {
                const next: InputTime = { d: 0, h: 0, m: 0 };
                update(calcMinutes(next));
                return next;
            });
        },
        countIcon: (ico: "add" | "minus") => {
            if (ico === "add") {
                const icon = minimized ? "add" : "chevron up";
                return <Icon name={icon} size="11px" marginTop={0} />;
            }
            if (ico === "minus") {
                const icon = minimized ? "minus" : "chevron down";
                return <Icon name={icon} size="11px" marginTop={0} />;
            }

        },
    }), [update, minimized]);

    React.useEffect(() => {
        setInputState(cur => {
            const next = inputStateFromValue(value);
            if (!_.isEqual(cur, next))
                return next;
            return cur;
        });
    }, [value]);

    const smallDevice = minimized || theme.content.width < breakpoints.tablet.width || shortenText;
    const miniDevice = useBelowBreakpoint("tablet");
    const titleMode = (title && info) ? "both" : (title && !info) ? "title" : (!title && info) ? "info" : undefined;

    const modify = React.useMemo(() => (curValue: number, changeCall: (val: string | undefined) => void, modifyBy: number = 1) => (ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (ev.keyCode === 38)
            changeCall(`${curValue + modifyBy}`);
        if (ev.keyCode === 40)
            changeCall(`${curValue - modifyBy}`);
    }, []);

    const modifyBtn = React.useMemo(() => (curValue: number, changeCall: (val: string | undefined) => void, modifyBy: number = 1, click: "up" | "down") => () => {
        if (click === "up")
            changeCall(`${curValue + modifyBy}`);
        if (click === "down")
            changeCall(`${curValue - modifyBy}`);
    }, []);

    const isMinimized = minimized && !miniDevice;
    const isMaximized = maximized || miniDevice;

    return (
        <div>
            {(title || info) &&
                <T.Title minimized={isMinimized ?? false} maximized={isMaximized ?? false} cleareable={cleareable ?? false} titleMode={titleMode} >
                    {title && <T.TitleText title={title}>{title}</T.TitleText>}
                    {info && <T.TitleInfo title={info} hasError={error}>{info}</T.TitleInfo>}
                </T.Title>
            }
            <T.Container maximized={isMaximized ?? false} minimized={isMinimized ?? false} cleareable={cleareable ?? false} miniDevice={miniDevice} error={error ?? false} noMinutes={noMinutes ?? false}>

                <T.Column miniDevice={miniDevice}>
                    <Input
                        kind='defaultTransparent'
                        maxLength={2}
                        value={inputState.d}
                        change={onChangeDays}
                        labelInside={miniDevice ? "days" : undefined}
                        onKeyDown={modify(inputState.d, onChangeDays)}
                        onWheel={(e) => {
                            const direction = e.deltaY < 0 ? 'up' : 'down';
                            if (direction === 'up') {
                                modifyBtn(inputState.d, onChangeDays, 1, "up")();
                            } else {
                                modifyBtn(inputState.d, onChangeDays, 1, "down")();
                            }
                            e.preventDefault();
                        }}
                        debounce={debounce}
                    />
                    {!miniDevice && !isMinimized && <T.Unit unit={smallDevice ? "d" : "days"} />}
                    {isMinimized &&
                        <T.UnitMinimized>days</T.UnitMinimized>
                    }
                    <T.CountUp onClick={modifyBtn(inputState.d, onChangeDays, 1, "up")} minimized={isMinimized}>{countIcon("add")}</T.CountUp>
                    <T.CountDown onClick={modifyBtn(inputState.d, onChangeDays, 1, "down")} minimized={isMinimized}>{countIcon("minus")}</T.CountDown>
                </T.Column>

                <T.Column miniDevice={miniDevice}>
                    <Input
                        kind='defaultTransparent'
                        maxLength={1}
                        value={inputState.h}
                        change={onChangeHours}
                        labelInside={miniDevice ? "hours" : undefined}
                        onKeyDown={modify(inputState.h, onChangeHours)}
                        onWheel={(e) => {
                            const direction = e.deltaY < 0 ? 'up' : 'down';
                            if (direction === 'up') {
                                modifyBtn(inputState.h, onChangeHours, 1, "up")();
                            } else {
                                modifyBtn(inputState.h, onChangeHours, 1, "down")();
                            }
                            e.preventDefault();
                        }}
                        debounce={debounce}
                    />
                    {!miniDevice && !isMinimized &&
                        <T.Unit unit={shortenText ? "h" : "hours"} />
                    }
                    {isMinimized &&
                        <T.UnitMinimized>hrs</T.UnitMinimized>
                    }
                    <T.CountUp onClick={modifyBtn(inputState.h, onChangeHours, 1, "up")} minimized={isMinimized}>{countIcon("add")}</T.CountUp>
                    <T.CountDown onClick={modifyBtn(inputState.h, onChangeHours, 1, "down")} minimized={isMinimized}>{countIcon("minus")}</T.CountDown>
                </T.Column>
                {!noMinutes &&
                    <T.Column miniDevice={miniDevice}>
                        <Input
                            kind='defaultTransparent'
                            maxLength={2}
                            value={inputState.m}
                            change={onChangeMinutes}
                            step={step ?? 15}
                            labelInside={miniDevice ? "minutes" : undefined}
                            onKeyDown={modify(inputState.m, onChangeMinutes, step ?? 15)}
                            onWheel={(e) => {
                                const direction = e.deltaY < 0 ? 'up' : 'down';
                                if (direction === 'up') {
                                    modifyBtn(inputState.m, onChangeMinutes, step ?? 15, "up")();
                                } else {
                                    modifyBtn(inputState.m, onChangeMinutes, step ?? 15, "down")();
                                }
                                e.preventDefault();
                            }}
                            debounce={debounce}
                        />
                        {!miniDevice && !isMinimized &&
                            <T.Unit unit={shortenText ? "m" : "minutes"} />
                        }
                        {isMinimized &&
                            <T.UnitMinimized>mins</T.UnitMinimized>
                        }
                        <T.CountUp onClick={modifyBtn(inputState.m, onChangeMinutes, step ?? 15, "up")} minimized={isMinimized}>{countIcon("add")}</T.CountUp>
                        <T.CountDown onClick={modifyBtn(inputState.m, onChangeMinutes, step ?? 15, "down")} minimized={isMinimized}>{countIcon("minus")}</T.CountDown>
                    </T.Column>
                }
                {cleareable &&
                    <T.Btn miniDevice={miniDevice} minimized={isMinimized} onClick={onClear}>
                        <Button kind="transparentButton" icon="delete" onClick={onClear} tooltip={{ tooltipText: 'clear input' }} />
                    </T.Btn>
                }
            </T.Container>
        </div>
    );
}
export default DurationInput;